'use client';
import { Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import type { ContentSuggestion, VehicleSuggestion } from '@//app/api/suggestions/combined';
import {
  InputSearchAdvanced,
  type InputSearchAdvancedScale,
  InputSearchAdvancedSearchHistoryOption,
  InputSearchAdvancedSuggestionOption,
  type InputSearchAdvancedVariant,
} from '@//components/InputSearchAdvanced';
import useFilterHistory from '@//data/filter-history/useFilterHistory';
import useRemoveFilterHistory from '@//data/filter-history/useRemoveFilterHistory';
import useCombinedSuggestions from '@//data/suggestions/useCombinedSuggestions';
import useDebouncedValue from '@//hooks/useDebouncedValue';
import useSuggestionFoldoutManager from '@//hooks/useSuggestionFoldoutManager';
import { SuggestionDropdown } from '../SuggestionDropdown';
import styles from './WebsiteSearch.module.scss';

interface WebsiteSearchProps {
  searchPage: string;
  articlesPage: string;
  id: string;
  variant?: InputSearchAdvancedVariant;
  scale?: InputSearchAdvancedScale;
  className?: string;
  placeholder?: string;
}

export const WebsiteSearch = ({
  searchPage,
  articlesPage,
  id,
  variant = 'on-fill',
  scale = 'lg',
  className,
  placeholder,
}: WebsiteSearchProps) => {
  const router = useRouter();
  const t = useTranslations('components.websiteSearch');
  const { control, register, getValues } = useForm({
    defaultValues: { q: '' },
  });

  const filterHistory = useFilterHistory();
  const removeFilterHistoryMutation = useRemoveFilterHistory();

  const q = useWatch({ control, name: 'q' });
  const debouncedQ = useDebouncedValue(q, 150);
  const combinedSuggestionsQuery = useCombinedSuggestions(debouncedQ);
  const combinedSuggestions = combinedSuggestionsQuery.data;

  const {
    vehicle_suggestions = [],
    content_suggestions = [],
    queryString,
  } = combinedSuggestions || {};

  const filteredFilterHistory = useMemo(() => (!q ? filterHistory : []), [q, filterHistory]);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    activeIndex,
    activeSection,
    activeItem,
    sections,
    showDropdown,
    onFocus,
    goToNext,
    goToPrev,
    resetSuggestions,
    resetActiveIndex,
  } = useSuggestionFoldoutManager({
    geoLocationSuggestions: [],
    vehicleSuggestions: vehicle_suggestions,
    articleSuggestions: content_suggestions,
    filterHistory: filteredFilterHistory,
    searchQuery: q,
    queryString,
    clickOutsideRef: inputRef,
  });

  const handleSuggestionClick = useCallback(
    (suggestion: VehicleSuggestion | ContentSuggestion) => {
      //setValue('q', '');
      inputRef.current?.querySelector('input')?.blur();
      resetSuggestions();
      if ('facet' in suggestion) {
        router.push(`${searchPage}?${suggestion.facet}`);
      } else if ('path' in suggestion) {
        router.push(`${articlesPage}${suggestion.path}`);
      }
    },
    [articlesPage, searchPage, router, resetSuggestions],
  );

  const handleHistoryRemove = useCallback(
    (id: string) => removeFilterHistoryMutation.mutate(id),
    [removeFilterHistoryMutation],
  );

  const handleHistoryClick = useCallback(
    (historyItem: { id: string; query: string; label: string; date: string }) => {
      //setValue('q', '');
      router.push(`${searchPage}?${historyItem.query}`);
      inputRef.current?.querySelector('input')?.blur();
      resetSuggestions();
    },
    [searchPage, router, resetSuggestions],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        sections.some((section) => section.items.length > 0)
      ) {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          goToNext();
        } else {
          goToPrev();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeItem) {
          if (activeItem.type === 'vehicle') {
            handleSuggestionClick(activeItem.item as VehicleSuggestion);
          } else if (activeItem.type === 'article') {
            handleSuggestionClick(activeItem.item as ContentSuggestion);
          } else if (activeItem.type === 'history') {
            handleHistoryClick(
              activeItem.item as {
                id: string;
                query: string;
                label: string;
                date: string;
              },
            );
          }
        } else {
          const currentQ = getValues('q');
          const params = new URLSearchParams();
          if (currentQ) {
            params.set('q', currentQ);
          }
          const queryString = params.toString();
          //setValue('q', '');
          inputRef.current?.querySelector('input')?.blur();
          resetSuggestions();
          router.push(`${searchPage}${queryString ? `?${queryString}` : ''}`);
        }
      }
    },
    [
      sections,
      goToNext,
      goToPrev,
      activeItem,
      handleSuggestionClick,
      handleHistoryClick,
      getValues,
      searchPage,
      router,
      resetSuggestions,
    ],
  );

  const loadingSuggestions = !!q && (q !== queryString || combinedSuggestionsQuery.isLoading);

  const handleButtonClick = useCallback(() => {
    router.push(searchPage);
  }, [router, searchPage]);

  /*
  const isOnSearchPage = useMemo(() => {
    return pathname === searchPage;
  }, [pathname, searchPage]);
  */

  return (
    <>
      {false && (
        <button
          onClick={handleButtonClick}
          type="button"
          title={t('searchButtonTitle')}
          className={styles.searchBarButton}
        >
          <Search aria-hidden="true" />
          {t('searchButton')}
        </button>
      )}

      <InputSearchAdvanced
        ref={inputRef}
        id={id}
        label={t('searchInputLabel')}
        variant={variant}
        scale={scale}
        register={register('q')}
        autoComplete="off"
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onClose={resetSuggestions}
        placeholder={placeholder}
        suggestionExpanded={showDropdown}
        suggestionListId={`${id}-suggestions-list`}
        suggestionActiveOptionId={
          activeIndex > -1 ? `${id}-suggestions-option-${activeIndex}` : undefined
        }
        loading={loadingSuggestions}
        className={className}
      >
        {showDropdown && (
          <SuggestionDropdown
            id={id}
            sections={sections}
            activeIndex={activeIndex}
            activeSection={activeSection}
            renderVehicle={(item, isActive) => (
              <InputSearchAdvancedSuggestionOption
                key={`${id}-vehicle-${item.label}`}
                id={`${id}-vehicle-${item.label}`}
                href={`${searchPage}?${item.facet}`}
                title={item.label}
                count={item.term_count}
                isActive={isActive}
                onClick={() => handleSuggestionClick(item)}
                onMouseEnter={resetActiveIndex}
              >
                {item.label}
              </InputSearchAdvancedSuggestionOption>
            )}
            renderArticle={(item, isActive) => (
              <InputSearchAdvancedSuggestionOption
                key={`${id}-article-${item.label}`}
                id={`${id}-article-${item.label}`}
                href={`${articlesPage}${item.path}`}
                title={item.label}
                isActive={isActive}
                onClick={() => handleSuggestionClick(item)}
                onMouseEnter={resetActiveIndex}
              >
                {item.label}
              </InputSearchAdvancedSuggestionOption>
            )}
            renderHistory={(item, isActive) => (
              <InputSearchAdvancedSearchHistoryOption
                key={item.id}
                label={item.label}
                title={item.label}
                href={`${searchPage}?${item.query}`}
                isActive={isActive}
                onRemove={() => handleHistoryRemove(item.id)}
                onClick={() => handleHistoryClick(item)}
                onMouseEnter={resetActiveIndex}
              />
            )}
          />
        )}
      </InputSearchAdvanced>
    </>
  );
};
